import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, List, ListItem } from '@mui/material';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { Delete, DragIndicator, Edit } from '@mui/icons-material';

const DraggableHeading = ({ heading }) => {
  const headingRef = useRef(null);

  useEffect(() => {
    const element = headingRef.current;
    if (!element) return;

    return draggable({
      element,
      getInitialData: () => ({ type: 'heading', heading }),
    });
  }, [heading]);

  return (
    <Box
      ref={headingRef}
      sx={{
        border: '1px solid lightgray',
        borderRadius: 2,
        p: 1,
        backgroundColor: 'white',
        cursor: 'grab',
      }}
    >
      <Typography variant="caption">{heading}</Typography>
    </Box>
  );
};

const TabListItem = ({
  tab,
  index,
  onReorder,
  handleUpdateTab,
  removeTab,
  handleNotesClick,
}) => {
  const liRef = useRef(null);
  const [newLabel, setNewLabel] = useState(tab.label);
  const [editLabel, setEditLabel] = useState(false);

  useEffect(() => {
    const element = liRef.current;
    if (!element) return;

    return combine(
      draggable({
        element,
        getInitialData: () => ({ type: 'tab', index }),
      }),
      dropTargetForElements({
        element,
        onDrop: ({ source }) => {
          if (source.data.type === 'tab' && source.data.index !== index) {
            onReorder(source.data.index, index);
          } else if (source.data.type === 'heading') {
            handleUpdateTab(index, {
              ...tab,
              headers: [...tab.headers, source.data.heading], // Add the dropped heading
            });
          }
        },
      })
    );
  }, [index, onReorder, handleUpdateTab, tab]);

  const removeHeader = (header) => {
    const updatedHeaders = tab.headers.filter((h) => h !== header);
    handleUpdateTab(index, { ...tab, headers: updatedHeaders });
  };

  return (
    <ListItem ref={liRef} sx={{ padding: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          border: '1px solid lightgray',
          borderRadius: 2,
          p: 1,
          flex: 1,
          backgroundColor: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <DragIndicator sx={{ cursor: 'grab' }} />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              justifyContent: 'space-between',
              mb: 1,
              alignItems: 'center',
            }}
          >
            {editLabel ? (
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                />
                <button
                  onClick={() => {
                    handleUpdateTab(index, { ...tab, label: newLabel });
                    setEditLabel(false);
                  }}
                >
                  Save
                </button>
                <button onClick={() => setEditLabel(false)}>Cancel</button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography>{tab.label}</Typography>
                {/* <EditIcon onClick={() => setEditLabel(true)} /> */}
                <Edit onClick={() => setEditLabel(true)} />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                onClick={() => handleNotesClick(index)}
                sx={{
                  padding: '2px 10px',
                  borderRadius: '4px',
                  backgroundColor: tab.useNotes ? '#FF6A00' : 'lightgrey',
                }}
              >
                <Typography
                  variant="caption"
                  color={tab.useNotes ? 'white' : 'gray'}
                >
                  Use notes
                </Typography>
              </Box>
              <Delete onClick={() => removeTab(index)} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 1,
              flex: 1,
              borderRadius: 2,
              border: '1px solid lightblue',
            }}
          >
            {tab.headers.map((header) => (
              <Box
                key={header}
                sx={{ backgroundColor: 'lightgrey', p: 1, borderRadius: 2 }}
                onClick={() => removeHeader(header)}
              >
                <Typography variant="caption">{header}</Typography>
              </Box>
            ))}
            {tab.headers.length === 0 && (
              <Typography variant="caption">No Sections in this tab</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

const DealFormTabsConfig = ({ formTabs, setFormTabs, availableHeadings }) => {
  const [createTab, setCreateTab] = useState(false);
  const [newTabLabel, setNewTabLabel] = useState('');
  const handleReorder = (fromIndex, toIndex) => {
    const updatedTabs = [...formTabs];
    const [movedTab] = updatedTabs.splice(fromIndex, 1); // Remove the dragged tab
    updatedTabs.splice(toIndex, 0, movedTab); // Insert at new position
    setFormTabs(updatedTabs);
  };

  const handleUpdateTab = (index, updatedTab) => {
    const updatedTabs = [...formTabs];
    updatedTabs[index] = updatedTab;
    setFormTabs(updatedTabs);
  };
  const removeTab = (index) => {
    const updatedTabs = formTabs.filter((tab, i) => i !== index);
    setFormTabs(updatedTabs);
  };
  const handleNotesClick = (index) => {
    let updatedTabs = [...formTabs];
    const status = updatedTabs[index].useNotes || false;
    if (status) {
      updatedTabs[index].useNotes = false;
    } else {
      updatedTabs = formTabs.map((tab, i) => {
        if (i === index) {
          return { ...tab, useNotes: true };
        }
        return { ...tab, useNotes: false };
      });
    }
    setFormTabs(updatedTabs);
  };

  return (
    <Box border="1px solid lightgray" p={2} borderRadius={2}>
      <Typography fontWeight="bold" fontSize="large" mb={2}>
        Deal Form Tabs
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {createTab ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="text"
              value={newTabLabel}
              onChange={(e) => setNewTabLabel(e.target.value)}
            />
            <button
              onClick={() => {
                setFormTabs([
                  ...formTabs,
                  {
                    label: newTabLabel,
                    headers: [],
                  },
                ]);
                setCreateTab(false);
                setNewTabLabel('');
              }}
            >
              Save
            </button>
            <button onClick={() => setCreateTab(false)}>Cancel</button>
          </Box>
        ) : (
          <button onClick={() => setCreateTab(true)}>Create New Tab</button>
        )}
      </Box>
      {/* Draggable Available Headings */}
      <Typography variant="caption">
        Drag available haeders to the wanted tab. click on headers in tabs to
        remove them from a tab
      </Typography>
      <Typography variant="h6">Available Headers</Typography>
      <Box sx={{ backgroundColor: 'lightblue', padding: '4px' }}>
        {availableHeadings.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableHeadings.map((heading) => (
              <DraggableHeading key={heading} heading={heading} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid lightgray',
              borderRadius: 2,
              p: 1,
              backgroundColor: 'white',
              mb: 2,
            }}
          >
            <Typography variant="caption">All headings are used</Typography>
          </Box>
        )}
      </Box>

      {/* Tabs List */}
      <List sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
        {formTabs.map((tab, index) => (
          <TabListItem
            key={tab.id || `tab-${index}`}
            tab={tab}
            index={index}
            onReorder={handleReorder}
            handleUpdateTab={handleUpdateTab}
            removeTab={removeTab}
            handleNotesClick={handleNotesClick}
          />
        ))}
      </List>
    </Box>
  );
};

export default DealFormTabsConfig;
